import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';

import apiOffers from 'api/offers';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import SearchDropdown from 'components/layout/forms/SearchDropdown';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import { RIGHT } from 'services/enums/sides';
import {
  LOST_REASON,
  toOptions,
  useChoiceList,
} from 'services/offers/offerChoices';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';

import CollapsibleOfferSection from './CollapsibleOfferSection';

export interface IOfferLost {
  info: null | string;
  reason: null | string;
  winner: null | { label: string; value: number };
}

/**
 * Of an offer gone to someone else (Ohi): why, the company that got the contract and
 * anything else about it. Opened and closed like the versions, and saved on its own, as
 * the offer itself is locked once sent.
 */
const OfferLost = ({
  disabled,
  lost,
  offerId,
  onSaved,
  searchCompanies,
}: {
  // E.g. the user can only read the offer
  disabled: boolean;
  lost: IOfferLost;
  offerId: number;
  onSaved: (lost: IOfferLost) => void;
  searchCompanies: (
    inputValue: string
  ) => Promise<{ label: string; value: number }[]>;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers, translations.common]);
  const text = (key: string) => t(`${translations.offers}:lost.${key}`);
  const dispatch = useDispatch();

  // Open when the status is chosen, so that it is filled in at once
  const [isOpen, setIsOpen] = useState(true);
  const [form, setForm] = useState<IOfferLost>(lost);
  const [saving, setSaving] = useState(false);
  const reasons = useChoiceList(LOST_REASON, form.reason);

  useEffect(() => setForm(lost), [lost]);

  const changed =
    form.reason !== lost.reason ||
    (form.info ?? '') !== (lost.info ?? '') ||
    (form.winner?.value ?? null) !== (lost.winner?.value ?? null);

  const save = async () => {
    setSaving(true);
    try {
      await apiOffers.patchLost(offerId, {
        info: form.info,
        reason: form.reason,
        winnerContactId: form.winner?.value ?? null,
      });
      onSaved(form);
      dispatch(addMessage(text('Saved')));
    } catch (error) {
      dispatch(addMessage(text('Save failed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <CollapsibleOfferSection
      isOpen={isOpen}
      title={text('Title')}
      toggle={() => setIsOpen(!isOpen)}
    >
      <Row className="offer-lost">
        <Col md={4}>
          <SelectInputFieldControlled
            disabled={disabled}
            id="lostReason"
            label={text('Reason')}
            name="lostReason"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              // Read before the event is reused (React 16)
              const { value } = event.target;
              setForm((current) => ({ ...current, reason: value || null }));
            }}
            options={toOptions(reasons)}
            value={form.reason ?? ''}
          />
        </Col>
        <Col md={8}>
          <FormGroup>
            <Label className="label" for="lostWinner">
              {text('Winner')}
            </Label>
            <SearchDropdown
              className="text-field search-field"
              disabled={disabled}
              id="lostWinner"
              loadOptions={searchCompanies}
              name="lostWinner"
              onChange={(winner: null | { label: string; value: number }) =>
                setForm((current) => ({ ...current, winner }))
              }
              placeholder={t(`${translations.common}:Search`)}
              value={form.winner}
            />
          </FormGroup>
        </Col>
        <Col md={12}>
          <FormGroup>
            <Label className="label" for="lostInfo">
              {text('Info')}
            </Label>
            <Input
              className="text-field"
              disabled={disabled}
              id="lostInfo"
              maxLength={4000}
              onChange={(event) => {
                const { value } = event.target;
                setForm((current) => ({ ...current, info: value }));
              }}
              placeholder={text('Info placeholder')}
              rows={3}
              type="textarea"
              value={form.info ?? ''}
            />
          </FormGroup>
        </Col>
        {disabled ? null : (
          <Col md={12}>
            <PrimaryButton
              disabled={saving || !changed}
              float={RIGHT}
              onClick={save}
            >
              {text('Save').toLocaleUpperCase()}
            </PrimaryButton>
          </Col>
        )}
      </Row>
    </CollapsibleOfferSection>
  );
};

export default OfferLost;
